import { Language } from '@/context/LanguageContext';
import { CityLanguage, cityLanguageNames } from '@/context/languages/languageData';

/**
 * Detect the user's preferred language from browser settings
 */
export const detectBrowserLanguage = (): Language => {
  // Get browser languages in order of preference
  const browserLanguages = navigator.languages || [navigator.language || 'en'];
  
  // Supported main languages for the site
  const supportedLanguages: Language[] = ['en', 'es', 'fr', 'de', 'it', 'pt'];
  
  // Check each browser language against supported ones
  for (const browserLang of browserLanguages) {
    // Extract language code (e.g., 'es-ES' -> 'es')
    const langCode = browserLang.toLowerCase().split('-')[0];
    
    // Check if we support this language
    if (supportedLanguages.includes(langCode as Language)) {
      return langCode as Language;
    }
  }
  
  // Default to English if no match found
  return 'en';
};

/**
 * Detect the user's preferred city language from browser settings
 * This includes all 21 supported languages for city pages
 */
export const detectBrowserCityLanguage = (): CityLanguage => {
  const browserLanguages = navigator.languages || [navigator.language || 'en'];
  
  // All supported city languages
  const supportedCityLanguages = Object.keys(cityLanguageNames) as CityLanguage[];
  
  // Check each browser language against supported ones
  for (const browserLang of browserLanguages) {
    const langCode = browserLang.toLowerCase().split('-')[0];
    
    if (supportedCityLanguages.includes(langCode as CityLanguage)) {
      return langCode as CityLanguage;
    }
  }
  
  return 'en';
};

/**
 * Get language preference from localStorage with browser fallback
 */
export const getStoredLanguagePreference = (): Language => {
  try {
    const stored = localStorage.getItem('preferred-language');
    if (stored && ['en', 'es', 'fr', 'de', 'it', 'pt'].includes(stored)) {
      return stored as Language;
    }
  } catch (error) {
    console.warn('Could not access localStorage for language preference');
  }
  
  // Fallback to browser detection
  return detectBrowserLanguage();
};

/**
 * Store language preference in localStorage
 */
export const storeLanguagePreference = (language: Language): void => {
  try {
    localStorage.setItem('preferred-language', language);
  } catch (error) {
    console.warn('Could not store language preference in localStorage');
  }
};

/**
 * Get the most appropriate language for a user
 * Priority: URL > localStorage > Browser > Default (en)
 */
export const getBestLanguageMatch = (urlLanguage?: string): Language => {
  const supportedLanguages: Language[] = ['en', 'es', 'fr', 'de', 'it', 'pt'];
  
  // 1. Check if URL language is valid
  if (urlLanguage && supportedLanguages.includes(urlLanguage as Language)) {
    return urlLanguage as Language;
  }
  
  // 2. Check stored preference
  const storedLang = getStoredLanguagePreference();
  if (supportedLanguages.includes(storedLang)) {
    return storedLang;
  }
  
  // 3. Browser detection
  const browserLang = detectBrowserLanguage();
  if (supportedLanguages.includes(browserLang)) {
    return browserLang;
  }
  
  // 4. Default fallback
  return 'en';
};

/**
 * Check if a language code is supported for main site navigation
 */
export const isMainLanguageSupported = (langCode: string): langCode is Language => {
  return ['en', 'es', 'fr', 'de', 'it', 'pt'].includes(langCode);
};

/**
 * Check if a language code is supported for city pages
 */
export const isCityLanguageSupported = (langCode: string): langCode is CityLanguage => {
  return Object.keys(cityLanguageNames).includes(langCode);
};